import { useState } from "react"

import { Download } from '@mui/icons-material'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'

import { type User } from "../../api"
import { downloadUserDataExport } from "../../lib/export-utils"

type ExportDataDialogProps = {
  user: User
}

export function ExportDataDialog({ user }: ExportDataDialogProps) {
  const [open, setOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleOpen = () => {
    setOpen(true)
    setError(null)
    setSuccess(false)
  }

  const handleClose = () => {
    if (!isExporting) {
      setOpen(false)
      setError(null)
      setSuccess(false)
    }
  }

  const handleExportData = async () => {
    try {
      setIsExporting(true)
      setError(null)
      setSuccess(false)
      
      await downloadUserDataExport(user.id)
      
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export user data')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="primary"
        startIcon={<Download />}
        onClick={(e) => {
          e.stopPropagation()
          handleOpen()
        }}
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          minWidth: 'auto',
          px: 1.5,
          py: 0.5,
          height: 28,
        }}
      >
        Export Data
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={isExporting}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Download />
          Export User Data (GDPR)
        </DialogTitle>

        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              GDPR Data Export
            </Typography>
            <Typography variant="body2" gutterBottom>
              This will download a password-protected ZIP file containing all user data in JSON format, 
              suitable for GDPR data portability requests.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'info.dark' }}>
              🔒 Password: AURA
            </Typography>
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              User Information:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Email: {user.email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Name: {user.firstName} {user.lastName}
            </Typography>
          </Box>

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Export will include:
          </Typography>
          <List dense disablePadding>
            <ListItem disablePadding>
              <ListItemText
                primary="User Account Information"
                secondary="Basic profile and account details"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem disablePadding>
              <ListItemText
                primary="Health Profiles"
                secondary="All health-related data and questionnaire responses"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem disablePadding>
              <ListItemText
                primary="Lab Orders"
                secondary="Order history and results"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem disablePadding>
              <ListItemText
                primary="AI Sessions & Responses"
                secondary="All AI-generated content and interactions"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem disablePadding>
              <ListItemText
                primary="Event Logs"
                secondary="Activity history and system events"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          {success && (
            <Alert severity="success" sx={{ mt: 3 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Export successful!
              </Typography>
              <Typography variant="body2" gutterBottom>
                The data has been downloaded to your computer.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                🔒 Password: AURA
              </Typography>
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} variant="outlined" disabled={isExporting}>
            {success ? 'Close' : 'Cancel'}
          </Button>
          {!success && (
            <Button
              onClick={handleExportData}
              variant="contained"
              color="primary"
              disabled={isExporting}
              startIcon={isExporting ? <CircularProgress size={16} /> : <Download />}
            >
              {isExporting ? 'Exporting...' : 'Export Data'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}

